import React, { useState, useEffect, useRef, useMemo } from 'react';
import { scrollAppToTop } from '../../../utils/scrollToTop';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Grid,
  Header,
  Image,
  Segment,
  Message,
  Loader,
  Button,
  Form,
  TextArea,
  Divider,
  Icon,
  Checkbox,
  Label,
  List,
  Pagination,
  Input,
  Dropdown
} from 'semantic-ui-react';
import {
  fetchOpportunity,
  selectSelectedOpportunity,
  selectOpportunitiesStatus,
  selectOpportunitiesError
} from '../../../slices/opportunityDataSlice';
import {
  createApplication,
  fetchActorApplications,
  selectActorApplications,
  selectApplicationsStatus,
  selectApplicationsError
} from '../../../slices/applicationSlice';
import {
  fetchPrimaryPortfolio,
  fetchSampleCharacters,
  selectPrimaryPortfolio,
  selectSampleCharacters
} from '../../../slices/sampleCharacterSlice';
import OpportunityStatusBadge from '../../../components/opportunities/OpportunityStatusBadge';
import { HeaderAndPage } from '../../../common/components';
import { CustomButton } from '../../../components/common';
import { format } from 'date-fns';
import docApply from '../../../assets/documents-apply.svg';
import '../../../styles/opportunities.css';
import { AuthStdFooter } from "../../../utils/auth-std-footer"
import { signS3UrlForUser } from '../../../utils/fetchSignedUrlByFolderV2';
import voiceActressDefault from '../../../assets/voiceActress.png';
import useSignedAvatar from '../../../hooks/useSignedAvatar';
import resetIcon from '../../../assets/reset-icon-a.svg';
import arrowLeft from '../../../assets/arrow-left.svg';
import applyIcon from '../../../assets/apply-icon.svg';

// Character Avatar Component with S3 URL signing
const CharacterAvatar = ({ avatarUrl, characterName, userId }) => {
  const { signedUrl } = useSignedAvatar(avatarUrl, userId, voiceActressDefault);

  return (
    <img
      src={signedUrl}
      alt={characterName || 'Character Avatar'}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }}
      onError={(e) => {
        e.target.src = voiceActressDefault;
      }}
    />
  );
};

/**
 * ApplicationSubmission component allows voice actors to apply for opportunities
 */
const ApplicationSubmission = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get back button text from location state
  const backButtonText = location.state?.backButtonText || 'Back';

  // Get opportunity from Redux store
  const opportunity = useSelector(selectSelectedOpportunity);
  const opportunityStatus = useSelector(selectOpportunitiesStatus);
  const opportunityError = useSelector(selectOpportunitiesError);

  // Get application status from Redux store
  const applicationStatus = useSelector(selectApplicationsStatus);
  const applicationError = useSelector(selectApplicationsError);

  // Get the current user and actor ID - consistent with VoiceSampleWorksheet
  const currentUser = useSelector(state => state.user.profile?.data);

  // For Voice Actors, actorId is often the same as userId
  // Match the pattern from VoiceSampleWorksheet.jsx
  const actorId = currentUser?.actorId || currentUser?.userId;
  const userId = currentUser?.userId;

  // Debug logging
  console.log('ApplicationSubmission: User data:', {
    currentUser,
    actorId,
    userId,
    hasActorId: !!actorId,
    hasUserId: !!userId
  });

  // Get applications for the current actor
  const actorApplications = useSelector(selectActorApplications(actorId || ''));

  // Get sample characters from the new Sample Character system
  const primaryPortfolio = useSelector(selectPrimaryPortfolio);
  const sampleCharacters = useSelector(selectSampleCharacters);

  // Check if the current actor has already applied for this opportunity
  const hasApplied = actorApplications.some(app => app.opportunity?.uuid === id);

  // State for form data
  const [formData, setFormData] = useState({
    notes: 'I am excited to apply for this voice acting opportunity. I have experience in character voices and would love to bring your story to life with my vocal talents.',
    sampleIds: []
  });

  // State for form validation
  const [formErrors, setFormErrors] = useState({});

  // State for available samples
  const [availableSamples, setAvailableSamples] = useState([]);

  // State for book cover URL
  const [bookCoverUrl, setBookCoverUrl] = useState('');

  // State and refs for audio playback
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const audioRefs = useRef({});

  // Pagination and search states
  const [activePage, setActivePage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    parseInt(localStorage.getItem('voiceSamplesPerPage')) || 3
  );
  const [searchTerm, setSearchTerm] = useState('');

  // Dummy samples to show when no real samples exist
  const dummySamples = [
    {
      id: 'dummy-1',
      name: 'Character Sample - Heroic Voice',
      character: 'Sample Character',
      variation: 'Heroic Voice',
      s3Url: null,
      isDummy: true
    },
    {
      id: 'dummy-2',
      name: 'Character Sample - Villain Voice',
      character: 'Sample Character',
      variation: 'Villain Voice',
      s3Url: null,
      isDummy: true
    },
    {
      id: 'dummy-3',
      name: 'Character Sample - Narrator Voice',
      character: 'Sample Character',
      variation: 'Narrator Voice',
      s3Url: null,
      isDummy: true
    }
  ];

  // Fetch opportunity, applications, and portfolio on component mount
  useEffect(() => {
    if (id) {
      dispatch(fetchOpportunity(id));

      if (actorId) {
        console.log('ApplicationSubmission: Fetching data for actorId:', actorId);
        dispatch(fetchActorApplications(actorId));
        // Always fetch fresh portfolio data to ensure we have the latest
        dispatch(fetchPrimaryPortfolio(actorId));
      }
    }
  }, [dispatch, id, actorId]);

  // Fetch sample characters when portfolio is loaded
  useEffect(() => {
    if (primaryPortfolio?.uuid) {
      console.log('ApplicationSubmission: Portfolio loaded, fetching sample characters for portfolio:', primaryPortfolio.uuid);
      // Force a fresh fetch of sample characters
      dispatch(fetchSampleCharacters(primaryPortfolio.uuid));
    } else if (primaryPortfolio === null && actorId) {
      console.log('ApplicationSubmission: No portfolio found, may need to create one');
    }
  }, [dispatch, primaryPortfolio?.uuid, actorId]);

  // Prepare available samples from Sample Characters with signed URLs
  useEffect(() => {
    const prepareSamplesWithSignedUrls = async () => {
      // Only process if we have sample characters and a userId
      if (sampleCharacters && sampleCharacters.length > 0 && userId) {
        console.log('Preparing samples with signed URLs:', sampleCharacters.length, 'characters found');

        const samplesWithSignedUrls = await Promise.all(
          sampleCharacters
            .filter(character => character.sample?.voiceS3Url) // Only include characters with voice samples
            .map(async (character) => {
              let signedUrl = null;
              try {
                signedUrl = await signS3UrlForUser(userId, character.sample.voiceS3Url);
              } catch (error) {
                console.error('Failed to sign URL for sample:', character.name, error);
              }

              return {
                id: character.uuid,
                name: character.name,
                description: character.description || '',
                character: character.name,
                variation: character.type || character.characterType || 'Default',
                s3Url: character.sample.voiceS3Url,
                signedUrl: signedUrl, // Add signed URL for playback
                textContent: character.sample.textContent || '',
                duration: character.sample.fileDuration || 0,
                gender: character.gender,
                ageGroup: character.ageGroup || character.age,
                // Additional fields for detailed display
                avatarUrl: character.avatarUrl,
                role: character.role,
                characterType: character.characterType,
                genre: character.genre,
                age: character.age
              };
            })
        );

        console.log('Setting available samples:', samplesWithSignedUrls.length);
        setAvailableSamples(samplesWithSignedUrls);
      } else if (sampleCharacters && sampleCharacters.length === 0) {
        // If we have fetched but there are no characters, clear the samples
        console.log('No sample characters found, clearing available samples');
        setAvailableSamples([]);
      }
    };

    prepareSamplesWithSignedUrls();
  }, [sampleCharacters, userId]);

  // Load book cover URL when opportunity is available
  useEffect(() => {
    const loadBookCover = async () => {
      if (opportunity?.book?.profile?.bookCover && userId) {
        try {
          const url = await signS3UrlForUser(userId, opportunity.book.profile.bookCover);
          setBookCoverUrl(url);
        } catch (error) {
          console.error('Failed to load book cover:', error);
        }
      }
    };

    loadBookCover();
  }, [opportunity?.book?.profile?.bookCover, userId]);

  // Handle form input changes
  const handleInputChange = (e, { name, value }) => {
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear validation error when field is updated
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Handle sample selection
  const handleSampleSelection = (e, { value, checked }) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        sampleIds: [...prev.sampleIds, value]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        sampleIds: prev.sampleIds.filter(id => id !== value)
      }));
    }

    // Clear validation error when samples are selected
    if (formErrors.sampleIds) {
      setFormErrors(prev => ({ ...prev, sampleIds: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.notes.trim()) {
      errors.notes = 'Please provide some notes about your application';
    }

    if (formData.sampleIds.length === 0) {
      errors.sampleIds = 'Please select at least one sample';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (validateForm()) {
      const applicationData = {
        actorId,
        opportunityId: id,
        notes: formData.notes,
        sampleIds: formData.sampleIds,
        status: 'Pending'
      };

      // Debug logging to track what samples are being sent
      console.log('[ApplicationSubmission] Submitting application with:', {
        actorId,
        opportunityId: id,
        selectedSampleIds: formData.sampleIds,
        numberOfSamples: formData.sampleIds.length,
        availableSamples: availableSamples.filter(s => formData.sampleIds.includes(s.id))
      });

      dispatch(createApplication(applicationData))
        .unwrap()
        .then((result) => {
          console.log('[ApplicationSubmission] Application created successfully:', result);
          navigate(`/opportunities/${id}`);
        })
        .catch(error => {
          console.error('Failed to submit application:', error);
        });
    }
  };

  // Handle audio playback toggle
  const handleAudioToggle = (sampleId) => {
    // Pause all other audio elements
    Object.keys(audioRefs.current).forEach(id => {
      if (id !== sampleId && audioRefs.current[id]) {
        audioRefs.current[id].pause();
      }
    });

    const audio = audioRefs.current[sampleId];
    if (audio) {
      if (currentlyPlaying === sampleId) {
        // Currently playing this sample, pause it
        audio.pause();
        setCurrentlyPlaying(null);
      } else {
        // Play this sample
        audio.play().catch(error => {
          console.error('Failed to play audio:', error);
        });
        setCurrentlyPlaying(sampleId);
      }
    }
  };

  // Format duration from seconds to mm:ss
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle back button click - navigate to previous page
  const handleBack = () => {
    navigate(-1);
  };

  // Handle reset button click - reset form to initial state
  const handleReset = () => {
    // Reset form data to initial state
    setFormData({
      notes: 'I am excited to apply for this voice acting opportunity. I have experience in character voices and would love to bring your story to life with my vocal talents.',
      sampleIds: []
    });

    // Clear any form errors
    setFormErrors({});

    // Reset pagination to first page
    setActivePage(1);

    // Clear search term
    setSearchTerm('');

    // Scroll the app's real scrolling element, not the document. `.main-content` owns the
    // scrollbar (styles/layout.css), so `window.scrollTo` moved nothing here.
    scrollAppToTop({ behavior: 'smooth' });
  };

  // Format the deadline date if it exists
  const formattedDeadline = opportunity?.deadline
    ? format(new Date(opportunity.deadline), 'MMMM d, yyyy')
    : 'No deadline';

  // Determine base samples (must be before any returns for hooks rules)
  const baseSamples = availableSamples.length > 0 ? availableSamples : dummySamples;
  const usingDummyData = availableSamples.length === 0;

  // Debug: track usingDummyData state
  console.log('[ApplicationSubmission] usingDummyData:', usingDummyData, 'availableSamples:', availableSamples.length, 'sampleCharacters:', sampleCharacters?.length);

  // Filter samples based on search term (must use useMemo before any returns)
  const filteredSamples = useMemo(() => {
    if (!searchTerm.trim()) return baseSamples;

    const term = searchTerm.toLowerCase();
    return baseSamples.filter(sample => {
      return (
        (sample.name && sample.name.toLowerCase().includes(term)) ||
        (sample.description && sample.description.toLowerCase().includes(term)) ||
        (sample.role && sample.role.toLowerCase().includes(term)) ||
        (sample.characterType && sample.characterType.toLowerCase().includes(term)) ||
        (sample.genre && sample.genre.toLowerCase().includes(term)) ||
        (sample.textContent && sample.textContent.toLowerCase().includes(term)) ||
        (sample.gender && sample.gender.toLowerCase().includes(term)) ||
        (sample.age && sample.age.toLowerCase().includes(term))
      );
    });
  }, [baseSamples, searchTerm]);

  // Calculate pagination (must use useMemo before any returns)
  const totalPages = Math.ceil(filteredSamples.length / itemsPerPage);
  const samplesToDisplay = useMemo(() => {
    const start = (activePage - 1) * itemsPerPage;
    const end = itemsPerPage === 999 ? filteredSamples.length : start + itemsPerPage;
    return filteredSamples.slice(start, end);
  }, [filteredSamples, activePage, itemsPerPage]);

  // Reset to page 1 when search changes (must use useEffect before any returns)
  useEffect(() => {
    setActivePage(1);
  }, [searchTerm, itemsPerPage]);

  // Save items per page preference (must use useEffect before any returns)
  useEffect(() => {
    localStorage.setItem('voiceSamplesPerPage', itemsPerPage.toString());
  }, [itemsPerPage]);

  // Handle pagination change
  const handlePageChange = (e, { activePage }) => {
    setActivePage(activePage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e, { value }) => {
    setItemsPerPage(value);
  };

  // Handle search input change
  const handleSearchChange = (e, { value }) => {
    setSearchTerm(value);
  };

  // Items per page options
  const itemsPerPageOptions = [
    { key: 3, value: 3, text: '3 per page' },
    { key: 6, value: 6, text: '6 per page' },
    { key: 9, value: 9, text: '9 per page' },
    { key: 'all', value: 999, text: 'Show all' }
  ];

  // Render loading state
  if ((opportunityStatus === 'loading' && !opportunity) || (applicationStatus === 'loading')) {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Apply for Opportunity"
        subHeaderText="Loading opportunity information..."
        doCenter={true}
      >
        <Segment basic textAlign="center" style={{ minHeight: '300px', marginBottom: "70px" }}>
          <Loader active size="large">Loading</Loader>
        </Segment>
        {AuthStdFooter()}
      </HeaderAndPage>
    );
  }

  // Render error state
  if (opportunityStatus === 'failed') {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Apply for Opportunity"
        subHeaderText="Error loading opportunity information"
        doCenter={true}
        style={{ marginBottom: "70px" }}
      >
        <Message negative>
          <Message.Header>Error Loading Opportunity</Message.Header>
          <p>{opportunityError}</p>
          <CustomButton
            color="blue"
            onClick={() => navigate(-1)}
            icon={arrowLeft}
            size="tiny"
            style={{ marginTop: '10px' }}
          >
            {backButtonText}
          </CustomButton>
        </Message>
        {AuthStdFooter()}
      </HeaderAndPage>
    );
  }

  // Render not found state
  if (!opportunity) {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Apply for Opportunity"
        subHeaderText="Opportunity not found"
        doCenter={true}
        style={{ marginBottom: "70px" }}
      >
        <Message warning>
          <Message.Header>Opportunity Not Found</Message.Header>
          <p>The opportunity you are trying to apply for does not exist or has been removed.</p>
          <CustomButton
            color="blue"
            onClick={() => navigate(-1)}
            icon={arrowLeft}
            size="tiny"
            style={{ marginTop: '10px' }}
          >
            {backButtonText}
          </CustomButton>
        </Message>
        {AuthStdFooter()}
      </HeaderAndPage>
    );
  }

  // Render already applied state
  if (hasApplied) {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Apply for Opportunity"
        subHeaderText={opportunity.title}
        doCenter={true}
        style={{ marginBottom: "70px" }}
      >
        <Message info>
          <Message.Header>Already Applied</Message.Header>
          <p>You have already applied for this opportunity.</p>
          <CustomButton
            color="blue"
            onClick={() => navigate(`/opportunities/${id}`, {
              state: {
                from: location.pathname,
                backButtonText: 'Back to Marketplace'
              }
            })}
            icon={arrowLeft}
            size="tiny"
            style={{ marginTop: '10px' }}
          >
            Back to Opportunity Details
          </CustomButton>
        </Message>
        {AuthStdFooter()}
      </HeaderAndPage>
    );
  }

  // Render closed opportunity state
  if (opportunity.status !== 'Open') {
    return (
      <HeaderAndPage
        headerImage={docApply}
        headerText="Apply for Opportunity"
        subHeaderText={opportunity.title}
        doCenter={true}
        style={{ marginBottom: "70px" }}
      >
        <Message warning>
          <Message.Header>Opportunity Not Open</Message.Header>
          <p>This opportunity is no longer accepting applications.</p>
          <CustomButton
            color="blue"
            onClick={() => navigate(`/opportunities/${id}`, {
              state: {
                from: location.pathname,
                backButtonText: 'Back to Marketplace'
              }
            })}
            icon={arrowLeft}
            size="tiny"
            style={{ marginTop: '10px' }}
          >
            Back to Opportunity Details
          </CustomButton>
        </Message>
        {AuthStdFooter()}
      </HeaderAndPage>
    );
  }

  // Render application form
  return (
    <HeaderAndPage
      headerImage={docApply}
      headerText="Apply for Opportunity"
      subHeaderText={opportunity.title}
      doCenter={true}

    >
      <div className="application-submission-container" style={{ marginBottom: "70px" }} >
        <Segment className="application-submission-form">
          <div className="application-submission-header">
            <Header as="h2">{opportunity.title}</Header>
            <div className="application-opportunity-status">
              <OpportunityStatusBadge
                status={opportunity.status}
                priority={opportunity.priority}
                deadline={opportunity.deadline}
                size="small"
              />
            </div>
          </div>

          <Divider />

          {/* Opportunity Details Section */}
          <Segment secondary style={{ marginBottom: '20px' }}>
            <Grid>
              <Grid.Row>
                <Grid.Column width={bookCoverUrl ? 14 : 16}>
                  <Header as="h3" style={{ marginTop: 0 }}>
                    Voice Acting - {opportunity.character?.name || 'Character'}
                  </Header>
                  <p style={{ fontSize: '1.1em', marginBottom: '10px' }}>
                    {opportunity.description || `Voice acting opportunity for the character "${opportunity.character?.name || 'Character'}" in the book "${opportunity.book?.name || 'Book Title'}".`}
                  </p>

                  <Grid columns={2} stackable>
                    <Grid.Column>
                      <List>
                        {opportunity.book && (
                          <List.Item>
                            <Icon name="book" />
                            <List.Content>
                              <strong>Book:</strong> {opportunity.book.name}
                            </List.Content>
                          </List.Item>
                        )}
                        {opportunity.book?.profile?.author && (
                          <List.Item>
                            <Icon name="user" />
                            <List.Content>
                              <strong>Author:</strong> {opportunity.book.profile.author}
                            </List.Content>
                          </List.Item>
                        )}
                      </List>
                    </Grid.Column>
                    <Grid.Column>
                      <List>
                        {opportunity.character && (
                          <List.Item>
                            <Icon name="user circle" />
                            <List.Content>
                              <strong>Character:</strong> {opportunity.character.name}
                            </List.Content>
                          </List.Item>
                        )}
                        {opportunity.compensationType && (
                          <List.Item>
                            <Icon name="dollar" />
                            <List.Content>
                              <strong>Type:</strong> {opportunity.compensationType}
                            </List.Content>
                          </List.Item>
                        )}
                        {opportunity.compensation && (
                          <List.Item>
                            <Icon name="money" />
                            <List.Content>
                              <strong>Compensation:</strong> {opportunity.compensation}
                            </List.Content>
                          </List.Item>
                        )}
                        <List.Item>
                          <Icon name="calendar" />
                          <List.Content>
                            <strong>Deadline:</strong> {formattedDeadline}
                          </List.Content>
                        </List.Item>
                      </List>
                    </Grid.Column>
                  </Grid>
                </Grid.Column>
                {bookCoverUrl && (
                  <Grid.Column width={2} textAlign="center" >
                    <div >
                      <img
                        src={bookCoverUrl}
                        style={{ maxHeight: '200px', objectFit: 'contain' }}
                      />
                      <div style={{ backgroundColor: "transparent" }}>Book Cover</div>
                    </div>
                  </Grid.Column>
                )}
              </Grid.Row>
            </Grid>
          </Segment>

          {/* Requirements Checklist */}
          <div className={`requirements-checklist ${usingDummyData ? 'warning' : ''}`}>
            <Header as="h3">
              <Icon name="info circle" />
              Application Requirements
            </Header>
            <List relaxed>
              <List.Item>
                <Icon name={actorId ? "check circle" : "times circle"} />
                <List.Content>
                  <strong>Voice Actor Profile:</strong> {actorId ? 'Active' : 'Not found'}
                  {!actorId && (
                    <span> - <Link to="/profile">Create your voice actor profile</Link></span>
                  )}
                </List.Content>
              </List.Item>
              <List.Item>
                <Icon name={availableSamples.length > 0 ? "check circle" : "times circle"} />
                <List.Content>
                  <strong>Voice Samples:</strong> {availableSamples.length > 0 ? `${availableSamples.length} sample(s) available` : 'No samples recorded'}
                  {availableSamples.length === 0 && (
                    <span> - <Link to="/voice-lab">Record samples in Voice Lab</Link></span>
                  )}
                </List.Content>
              </List.Item>
              <List.Item>
                <Icon name={opportunity.status === 'Open' ? "check circle" : "times circle"} />
                <List.Content>
                  <strong>Opportunity Status:</strong> {opportunity.status}
                  {opportunity.status !== 'Open' && ' - This opportunity is no longer accepting applications'}
                </List.Content>
              </List.Item>
              <List.Item>
                <Icon name={!hasApplied ? "check circle" : "times circle"} />
                <List.Content>
                  <strong>Prior Application:</strong> {hasApplied ? 'Already applied' : 'No prior application'}
                  {hasApplied && ' - You can only apply once per opportunity'}
                </List.Content>
              </List.Item>
            </List>
            {(usingDummyData || hasApplied || opportunity.status !== 'Open' || !actorId) && (
              <div className="requirements-warning">
                <div className="header">Unable to Submit Application</div>
                <p>Please resolve all requirements marked with <Icon name="times circle" /> before submitting.</p>
              </div>
            )}
          </div>

          <Divider />

          <Form onSubmit={handleSubmit}>
            {applicationError && (
              <Message negative className="application-error-message">
                <Message.Header>Error Submitting Application</Message.Header>
                <p>{applicationError}</p>
              </Message>
            )}

            <Form.Field
              control={TextArea}
              label="Application Notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Introduce yourself and explain why you're a good fit for this opportunity..."
              rows={6}
              error={formErrors.notes ? { content: formErrors.notes, pointing: 'below' } : null}
              disabled={usingDummyData}
              className="application-notes-field"
            />

            <div className="voice-samples-section">
              <Header as="h3">Select Your Voice Actor Samples</Header>
              <p>Choose voice samples from your account to include with your application. These will help the book creator evaluate your voice.</p>

              {usingDummyData && (
                <Segment placeholder style={{ textAlign: 'center', padding: '2em' }}>
                  <Header icon>
                    <Icon name="microphone slash" color="orange" />
                    No Voice Samples Found
                  </Header>
                  <p style={{ fontSize: '1.1em', maxWidth: '500px', margin: '0 auto 1em' }}>
                    You need to record voice samples before you can apply for opportunities.
                    Head to the Voice Lab to create your first sample character with a voice recording.
                  </p>
                  <Button
                    color="blue"
                    size="large"
                    icon="microphone"
                    content="Record Your Samples"
                    onClick={() => navigate('/dashboard', {
                      state: {
                        menuSelection: 'VOICE-SAMPLE-WORKSHEET',
                        newCharacter: true,
                        returnTo: location.pathname
                      }
                    })}
                  />
                </Segment>
              )}

              {formErrors.sampleIds && !usingDummyData && (
                <Message negative className="application-error-message">
                  <p>{formErrors.sampleIds}</p>
                </Message>
              )}

              {!usingDummyData && (
                <Segment>
                  {/* Controls Section */}
                  <Grid style={{ marginBottom: '15px' }}>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        {/* Showing count */}
                        <div style={{ paddingTop: '8px', fontSize: '1.1em', color: '#666' }}>
                          {filteredSamples.length > 0 ? (
                            <>
                              Showing {Math.min((activePage - 1) * itemsPerPage + 1, filteredSamples.length)}-
                              {Math.min(activePage * itemsPerPage, filteredSamples.length)} of {filteredSamples.length} samples
                            </>
                          ) : searchTerm ? (
                            'No samples match your search'
                          ) : (
                            'No samples available'
                          )}
                        </div>
                      </Grid.Column>
                      <Grid.Column width={8} textAlign="right">
                        <div style={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
                          {/* Clear search button - only visible when there's search text */}
                          {searchTerm && (
                            <Button
                              size="small"
                              basic
                              icon="times"
                              content="Clear"
                              onClick={() => setSearchTerm('')}
                              title="Clear search"
                            />
                          )}
                          {/* Search input */}
                          <Input
                            icon="search"
                            placeholder="Search samples..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            style={{ minWidth: '200px' }}
                          />
                          {/* Items per page dropdown */}
                          <Dropdown
                            selection
                            compact
                            value={itemsPerPage}
                            options={itemsPerPageOptions}
                            onChange={handleItemsPerPageChange}
                            style={{ minWidth: '120px' }}
                          />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>

                  <Divider />

                  {/* Samples Grid */}
                  <Grid columns={1} stackable>
                    {samplesToDisplay.map(sample => (
                      <Grid.Column key={sample.id}>
                        <Segment className={`voice-sample-card ${sample.isDummy ? 'dummy-sample' : ''} ${formData.sampleIds.includes(sample.id) ? 'selected' : ''}`}>
                          <Grid>
                            <Grid.Row>
                              {/* Avatar and Selection Column */}
                              <Grid.Column width={2} textAlign="center">
                                {/* Character Avatar */}
                                <div style={{
                                  width: '80px',
                                  height: '80px',
                                  borderRadius: '50%',
                                  overflow: 'hidden',
                                  margin: '0 auto 10px',
                                  border: '2px solid #ddd'
                                }}>
                                  {!sample.isDummy ? (
                                    <CharacterAvatar
                                      avatarUrl={sample.avatarUrl}
                                      characterName={sample.name}
                                      userId={userId}
                                    />
                                  ) : (
                                    <img src={voiceActressDefault} alt="Default Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  )}
                                </div>
                                {/* Selection Checkbox */}
                                <div style={{ textAlign: 'center', marginTop: '5px' }}>
                                  <div style={{ fontSize: '0.85em', color: '#555', marginBottom: '5px', lineHeight: '1.2' }}>
                                    Select<br />Sample
                                  </div>
                                  <Checkbox
                                    checked={formData.sampleIds.includes(sample.id)}
                                    onChange={handleSampleSelection}
                                    value={sample.id}
                                    disabled={sample.isDummy}
                                  />
                                </div>
                              </Grid.Column>

                              {/* Character Information Column */}
                              <Grid.Column width={10}>
                                <Header as="h4" style={{ marginBottom: '5px' }}>
                                  {sample.name}
                                  {sample.role && <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666' }}> - {sample.role}</span>}
                                </Header>

                                {/* Character Metadata */}
                                <div style={{ marginBottom: '10px' }}>
                                  {sample.characterType && (
                                    <Label size="tiny" basic>
                                      <Icon name="users" />
                                      {sample.characterType}
                                    </Label>
                                  )}
                                  {sample.genre && (
                                    <Label size="tiny" basic>
                                      <Icon name="book" />
                                      {sample.genre}
                                    </Label>
                                  )}
                                  {sample.gender && (
                                    <Label size="tiny" basic>
                                      <Icon name="venus mars" />
                                      {sample.gender}
                                    </Label>
                                  )}
                                  {sample.age && (
                                    <Label size="tiny" basic>
                                      <Icon name="calendar outline" />
                                      {sample.age}
                                    </Label>
                                  )}
                                </div>

                                {/* Description */}
                                {sample.description && (
                                  <p style={{ fontSize: '0.9em', marginBottom: '10px', color: '#444' }}>
                                    {sample.description.length > 150
                                      ? `${sample.description.substring(0, 150)}...`
                                      : sample.description}
                                  </p>
                                )}

                                {/* Script Content */}
                                {sample.textContent && (
                                  <div style={{
                                    backgroundColor: '#f8f8f8',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    marginTop: '10px'
                                  }}>
                                    <div >Script Content</div>
                                    <p style={{
                                      fontSize: '0.85em',
                                      fontStyle: 'italic',
                                      margin: '5px 0 0 0',
                                      color: '#555'
                                    }}>
                                      "{sample.textContent.length > 200
                                        ? `${sample.textContent.substring(0, 200)}...`
                                        : sample.textContent}"
                                    </p>
                                  </div>
                                )}
                              </Grid.Column>

                              {/* Audio Controls Column */}
                              <Grid.Column width={4} textAlign="center">
                                <div style={{ paddingTop: '20px' }}>
                                  <div style={{ fontSize: '0.85em', color: '#555', marginBottom: '10px', lineHeight: '1.2' }}>
                                    Preview<br />Voice
                                  </div>
                                  {sample.signedUrl ? (
                                    <>
                                      <audio
                                        ref={el => audioRefs.current[sample.id] = el}
                                        src={sample.signedUrl}
                                        onEnded={() => setCurrentlyPlaying(null)}
                                        style={{ display: 'none' }}
                                      />
                                      <Button
                                        type="button"
                                        icon={currentlyPlaying === sample.id ? "pause" : "play"}
                                        circular
                                        size="large"
                                        primary
                                        onClick={() => handleAudioToggle(sample.id)}
                                        title={currentlyPlaying === sample.id ? "Pause" : "Play"}
                                      />
                                      <div style={{ marginTop: '10px', fontSize: '1.1em', fontWeight: 'bold' }}>
                                        {formatDuration(sample.duration)}
                                      </div>
                                    </>
                                  ) : sample.isDummy ? (
                                    <>
                                      <Button
                                        type="button"
                                        icon="play"
                                        circular
                                        size="large"
                                        disabled
                                        title="Sample not available - create your own in Voice Lab"
                                      />
                                      <Label size="small" color="grey" style={{ marginTop: '10px' }}>
                                        Preview Only
                                      </Label>
                                    </>
                                  ) : null}
                                </div>
                              </Grid.Column>
                            </Grid.Row>
                          </Grid>
                        </Segment>
                      </Grid.Column>
                    ))}
                  </Grid>

                  {/* Pagination Bar */}
                  {totalPages > 1 && (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <Pagination
                        activePage={activePage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        ellipsisItem={{ content: '...', icon: 'ellipsis horizontal' }}
                        firstItem={{ content: '«', icon: 'angle double left' }}
                        lastItem={{ content: '»', icon: 'angle double right' }}
                        prevItem={{ content: '‹', icon: 'angle left' }}
                        nextItem={{ content: '›', icon: 'angle right' }}
                      />
                    </div>
                  )}
                </Segment>
              )}
            </div>

            <Divider />

            <Message info className="application-agreement">
              <Message.Header>Before You Submit</Message.Header>
              <p>By submitting this application, you agree to the following:</p>
              <List bulleted>
                <List.Item>Your voice samples will be shared with the book creator</List.Item>
                <List.Item>The book creator may contact you for additional information</List.Item>
                <List.Item>If selected, you agree to provide voice recordings according to the opportunity requirements</List.Item>
              </List>
            </Message>

            <div className="application-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <CustomButton
                color="grey"

                type="button"
                onClick={handleBack}
                icon={arrowLeft}
                size="tiny"

              >
                {backButtonText}
              </CustomButton>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <CustomButton
                  type="button"
                  color="violet"
                  onClick={handleReset}
                  icon={resetIcon}
                  size="tiny"

                // disabled={formData.notes === 'I am excited to apply for this voice acting opportunity. I have experience in character voices and would love to bring your story to life with my vocal talents.' && formData.sampleIds.length === 0}
                >
                  Reset
                </CustomButton>

                <CustomButton
                  color="green"
                  type="submit"
                  loading={applicationStatus === 'loading'}
                  disabled={applicationStatus === 'loading' || usingDummyData || formData.sampleIds.length === 0}
                  icon={applyIcon}
                  size="tiny"

                  className="application-submit-button"
                >
                  {usingDummyData ? 'Submit Application (Disabled)' : 'Submit Application'}
                </CustomButton>

                {!usingDummyData && (
                  <div style={{
                    display: 'inline-block',
                    fontSize: '1.1em',
                    color: formData.sampleIds.length > 0 ? '#21ba45' : '#db2828'
                  }}>
                    <Icon name={formData.sampleIds.length > 0 ? "check circle" : "warning circle"} />
                    {formData.sampleIds.length === 0 ? (
                      'No samples selected'
                    ) : (
                      `${formData.sampleIds.length} sample${formData.sampleIds.length !== 1 ? 's' : ''} selected`
                    )}
                  </div>
                )}
              </div>
            </div>
          </Form>
        </Segment>
      </div>
      {AuthStdFooter()}

    </HeaderAndPage>
  );
};

export default ApplicationSubmission;
